// FinanceOverview — the Balance Sheet dashboard.
//
// Three visual layers:
//   1. Hero KPIs — Total Income · Total Expenses · Net · Cash on Hand
//   2. Category breakdown — two-column view of income by category and
//      expenses by category, each row with an inline bar for share.
//   3. Cash on Hand — one card per bank / wallet account, with the
//      latest snapshot and a "Log Balance" button.
//
// Every dollar comes from the /finance/summary aggregated endpoint so
// it stays in sync as records are added elsewhere.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight,
  Plus, X, Building2, Percent, CircleAlert
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import {
  fmtMoney, fmtDate, todayIso,
  CATEGORY_LABEL, CATEGORY_TONE, CATEGORY_TONES
} from "./shared";

export default function FinanceOverview({ seasonId, activeSeason, canWrite, refresh }) {
  const toast = useToast();
  const [summary,      setSummary]      = useState(null);
  const [accounts,     setAccounts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showAccount,  setShowAccount]  = useState(false);
  const [snapAccount,  setSnapAccount]  = useState(null); // Account being snapshotted

  async function loadAll() {
    if (!seasonId) return;
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.getFinanceSummary(seasonId),
        api.listCashAccounts(seasonId)
      ]);
      setSummary(s);
      setAccounts(a);
    } catch (err) {
      toast.error(err.message || "Couldn't load balance sheet.");
    } finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [seasonId]);

  const income   = summary?.income   || { total: 0, teamFees: [] };
  const expenses = summary?.expenses || { total: 0, byCategory: [], umpPaid: 0, umpOwed: 0 };
  const net      = summary?.net      || 0;
  const cash     = summary?.cashOnHand || 0;
  const netPositive = net >= 0;
  const reconDiff = cash - net;
  const reconAlert = Math.abs(reconDiff) > 0.5;

  // Compose expense rows with umpiring inlined so the category view
  // reads like the workbook's Expenses column.
  const expenseRows = useMemo(() => {
    const rows = [...(expenses.byCategory || []).map(r => ({
      category: r.category, count: r.count, total: r.total
    }))];
    if (expenses.umpPaid > 0) {
      rows.push({
        category: "Umpiring",
        count: null,
        total: expenses.umpPaid
      });
    }
    return rows.sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [expenses]);

  const expenseMax = Math.max(1, ...expenseRows.map(r => r.total || 0));
  const incomeMax  = Math.max(1, ...(income.teamFees || []).map(r => r.total || 0));

  return (
    <div className="stack" style={{gap:20}}>
      {loading && <p style={{color:"var(--muted)"}}>Loading balance sheet…</p>}

      {!loading && (
        <>
          {/* ── Hero KPIs ────────────────────────────────────── */}
          <div className="fin-kpis">
            <KpiCard tone="green" icon={TrendingUp}   label="Total Income"   value={income.total}/>
            <KpiCard tone="red"   icon={TrendingDown} label="Total Expenses" value={expenses.total}/>
            <KpiCard
              tone={netPositive ? "gold" : "red"}
              icon={netPositive ? ArrowUpRight : ArrowDownRight}
              label="Net"
              value={net}
              signed
            />
            <KpiCard tone="cyan"  icon={Wallet}       label="Cash on Hand"   value={cash}/>
          </div>

          {reconAlert && (
            <div className="fin-recon">
              <CircleAlert size={18}/>
              <div>
                <b>Reconciliation gap: {fmtMoney(Math.abs(reconDiff))}</b>
                <small>
                  Computed net is {fmtMoney(net)}, cash on hand is {fmtMoney(cash)}.
                  Log a fresh balance below to close the gap.
                </small>
              </div>
            </div>
          )}

          {/* ── Category breakdown ──────────────────────────── */}
          <div className="fin-breakdown">
            <Panel title={<><TrendingUp size={18}/> Income Breakdown</>}>
              {income.teamFees.length === 0 && (
                <p style={{color:"var(--muted)"}}>No income logged for this season yet.</p>
              )}
              <div className="fin-ledger">
                {income.teamFees.map(r => {
                  const tone = CATEGORY_TONE(r.category);
                  const share = incomeMax > 0 ? (r.total / incomeMax) * 100 : 0;
                  return (
                    <div key={r.category} className={`fin-ledger-row fin-ledger-row--${tone}`}>
                      <div className="fin-ledger-name">
                        <span className="fin-ledger-dot" style={{background: CATEGORY_TONES[tone]}}/>
                        <b>{CATEGORY_LABEL(r.category)}</b>
                        {r.count > 0 && <small>{r.count} entries</small>}
                      </div>
                      <div className="fin-ledger-bar">
                        <div className="fin-ledger-bar-fill" style={{
                          width: `${share}%`,
                          background: CATEGORY_TONES[tone]
                        }}/>
                      </div>
                      <div className="fin-ledger-amount">{fmtMoney(r.total)}</div>
                    </div>
                  );
                })}
                {income.teamFees.length > 0 && (
                  <div className="fin-ledger-row fin-ledger-row--total">
                    <div className="fin-ledger-name"><b>Total Income</b></div>
                    <div className="fin-ledger-bar"/>
                    <div className="fin-ledger-amount">{fmtMoney(income.total)}</div>
                  </div>
                )}
              </div>
            </Panel>

            <Panel title={<><TrendingDown size={18}/> Expenses Breakdown</>}>
              {expenseRows.length === 0 && (
                <p style={{color:"var(--muted)"}}>No expenses logged for this season yet.</p>
              )}
              <div className="fin-ledger">
                {expenseRows.map(r => {
                  const tone = r.category === "Umpiring" ? "gold" : "violet";
                  const share = expenseMax > 0 ? (r.total / expenseMax) * 100 : 0;
                  return (
                    <div key={r.category} className={`fin-ledger-row fin-ledger-row--${tone}`}>
                      <div className="fin-ledger-name">
                        <span className="fin-ledger-dot" style={{background: CATEGORY_TONES[tone]}}/>
                        <b>{r.category}</b>
                        {r.count != null && <small>{r.count} entries</small>}
                      </div>
                      <div className="fin-ledger-bar">
                        <div className="fin-ledger-bar-fill" style={{
                          width: `${share}%`,
                          background: CATEGORY_TONES[tone]
                        }}/>
                      </div>
                      <div className="fin-ledger-amount">{fmtMoney(r.total)}</div>
                    </div>
                  );
                })}
                {expenseRows.length > 0 && (
                  <div className="fin-ledger-row fin-ledger-row--total">
                    <div className="fin-ledger-name"><b>Total Expenses</b></div>
                    <div className="fin-ledger-bar"/>
                    <div className="fin-ledger-amount">{fmtMoney(expenses.total)}</div>
                  </div>
                )}
                {expenses.umpOwed > 0 && (
                  <div className="fin-ledger-row fin-ledger-row--pending">
                    <div className="fin-ledger-name">
                      <b>Umpiring — still owed</b>
                      <small>Not yet paid</small>
                    </div>
                    <div className="fin-ledger-bar"/>
                    <div className="fin-ledger-amount">{fmtMoney(expenses.umpOwed)}</div>
                  </div>
                )}
              </div>
            </Panel>
          </div>

          {/* ── Cash on hand ──────────────────────────────── */}
          <Panel title={
            <div style={{display:"flex", alignItems:"center", gap:10, flex:1}}>
              <Wallet size={18}/>
              <span>Cash on Hand</span>
              {canWrite && (
                <button
                  type="button"
                  className="fin-newAccountBtn"
                  onClick={() => setShowAccount(true)}
                  style={{marginLeft:"auto"}}
                >
                  <Plus size={13}/> Add Account
                </button>
              )}
            </div>
          }>
            {accounts.length === 0 && (
              <div className="league-annEmpty">
                <Building2 size={26}/>
                <p>
                  No cash accounts yet.{canWrite ? " Add a Bank / PayPal / CashApp account to start tracking balances." : ""}
                </p>
              </div>
            )}
            <div className="fin-cashGrid">
              {accounts.map(a => (
                <article key={a.id} className="fin-cashCard">
                  <header>
                    <Building2 size={14}/>
                    <span>{a.name}</span>
                    {a.latestAsOfDate && (
                      <em>as of {fmtDate(a.latestAsOfDate)}</em>
                    )}
                  </header>
                  <div className="fin-cashCard-value">
                    {fmtMoney(a.latestBalance != null ? a.latestBalance : a.opening)}
                  </div>
                  {canWrite && (
                    <button
                      type="button"
                      className="fin-cashCard-btn"
                      onClick={() => setSnapAccount(a)}
                    >
                      <Percent size={11}/> Log Balance
                    </button>
                  )}
                </article>
              ))}
            </div>
          </Panel>
        </>
      )}

      {showAccount && (
        <NewAccountModal
          seasonId={seasonId}
          onClose={() => setShowAccount(false)}
          onCreated={() => { setShowAccount(false); loadAll(); }}
        />
      )}
      {snapAccount && (
        <SnapshotModal
          account={snapAccount}
          onClose={() => setSnapAccount(null)}
          onSaved={() => { setSnapAccount(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ tone, icon: Icon, label, value, signed }) {
  const display = signed
    ? `${value < 0 ? "-" : ""}${fmtMoney(Math.abs(value))}`
    : fmtMoney(value);
  return (
    <article className={`fin-kpi fin-kpi--${tone}`}>
      <span className="fin-kpi-icon"><Icon size={18}/></span>
      <span className="fin-kpi-label">{label}</span>
      <b className="fin-kpi-value">{display}</b>
    </article>
  );
}

function NewAccountModal({ seasonId, onClose, onCreated }) {
  const toast = useToast();
  const [name,    setName]    = useState("Bank");
  const [opening, setOpening] = useState("");
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) { toast.error("Account name required."); return; }
    setBusy(true);
    try {
      await api.createCashAccount({
        seasonId, accountName: name.trim(),
        openingBalance: Number(opening) || 0
      });
      toast.success("Account added.");
      onCreated();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  return createPortal((
    <div className="modal" onClick={onClose}>
      <div className="modalInner" onClick={e => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose} aria-label="Close">
          <X size={20}/>
        </button>
        <Panel title="Add Cash Account">
          <form className="form" onSubmit={submit}>
            <label>Account Name *</label>
            <input list="fin-account-suggestions"
              value={name} onChange={e => setName(e.target.value)}
              placeholder="Bank / PayPal / CashApp / Venmo…" autoFocus/>
            <datalist id="fin-account-suggestions">
              {["Bank", "PayPal", "CashApp", "Venmo", "Zelle Reserve", "Petty Cash"].map(n => (
                <option key={n} value={n}/>
              ))}
            </datalist>
            <label>Opening Balance</label>
            <input type="number" step="0.01" value={opening}
              onChange={e => setOpening(e.target.value)}
              placeholder="0.00"/>
            <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:6}}>
              <button type="button" className="dark" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Saving…" : "Add Account"}
              </button>
            </div>
          </form>
        </Panel>
      </div>
    </div>
  ), document.body);
}

function SnapshotModal({ account, onClose, onSaved }) {
  const toast = useToast();
  const [balance, setBalance] = useState(
    account.latestBalance != null ? String(account.latestBalance) : String(account.opening ?? 0)
  );
  const [asOf,    setAsOf]    = useState(todayIso());
  const [notes,   setNotes]   = useState("");
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createCashSnapshot({
        accountId: account.id,
        balance: Number(balance),
        asOfDate: asOf,
        notes: notes || null
      });
      toast.success("Balance logged.");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  return createPortal((
    <div className="modal" onClick={onClose}>
      <div className="modalInner" onClick={e => e.stopPropagation()}>
        <button type="button" className="close" onClick={onClose} aria-label="Close">
          <X size={20}/>
        </button>
        <Panel title={`Log Balance · ${account.name}`}>
          <form className="form" onSubmit={submit}>
            <div className="two">
              <div>
                <label>Balance *</label>
                <input type="number" step="0.01" required autoFocus
                  value={balance} onChange={e => setBalance(e.target.value)}/>
              </div>
              <div>
                <label>As Of *</label>
                <input type="date" required
                  value={asOf} onChange={e => setAsOf(e.target.value)}/>
              </div>
            </div>
            <label>Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional — statement close, deposit slip, etc."/>
            <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:6}}>
              <button type="button" className="dark" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Saving…" : "Log Balance"}
              </button>
            </div>
          </form>
        </Panel>
      </div>
    </div>
  ), document.body);
}
